import React, { useEffect } from 'react';
import { EmailTemplate } from '../../../../../../entities/EmailTemplate';
import { LabelledSelect } from '../../../../../../components/LabelledSelect';
import useOptions from '../../../../../../hooks/useOptions';
import { useCalendarEventTemplates } from '../../../../../../queries/v1/useCalendarEventTemplates';
import { IconButton } from '../../../../../../components/IconButton';
import { Size } from '../../../../../../utils/types/Size';
import classNames from 'classnames';
import { useEmailTemplate } from '../../../../../../queries/useEmailTemplate';

interface PropTypes {
  calendarEventTemplate: EmailTemplate;
  title?: string;
  size?: Size;
  labelClassName?: string;
  isEditingCalendarEventTemplate?: boolean;
  allowEditingCalendarEventTemplate?: boolean;
  emailTemplateId?: number;
  setIsEditingCalendarEventTemplate?: (isEditing: boolean) => void;
  setCalendarEventTemplate: (option: EmailTemplate) => void;
}

const calendarEventToOption = (template: EmailTemplate) => {
  if (!template) return null;

  return {
    label: template.name,
    value: template.id.toString(),
  };
};

export function CalendarEventSelector(props: PropTypes) {
  const { data: emailTemplate } = useEmailTemplate(props.emailTemplateId);
  const { data: calendarEvents, isLoading } = useCalendarEventTemplates();
  const options = useOptions(
    calendarEvents?.emailTemplates ?? [],
    calendarEventToOption,
  );

  useEffect(() => {
    if (!props.calendarEventTemplate) {
      props.setCalendarEventTemplate(
        calendarEvents?.emailTemplates.find((template) => template.default),
      );
    }
  }, [props.calendarEventTemplate, calendarEvents?.emailTemplates]);

  useEffect(() => {
    props.setCalendarEventTemplate(emailTemplate);
  }, [emailTemplate]);

  return (
    <>
      <div
        data-testid='interview_calendar_event_selector'
        className='d-flex align-items-end'
      >
        <LabelledSelect
          disabled={isLoading || props.isEditingCalendarEventTemplate}
          options={options}
          label={props.title || 'Calendar Event'}
          size={props.size || 'sm'}
          labelClassName={classNames(
            'text-dark-500 fs-5',
            props.labelClassName,
          )}
          selected={calendarEventToOption(props.calendarEventTemplate)}
          className='me-3'
          onChange={(option) =>
            props.setCalendarEventTemplate(
              calendarEvents?.emailTemplates.find(
                (template) => template.id.toString() == option.value,
              ),
            )
          }
        />
        {props.allowEditingCalendarEventTemplate && (
          <>
            {props.isEditingCalendarEventTemplate ? (
              <h6 className='text-blue-200 mb-1'>Editing...</h6>
            ) : (
              <IconButton
                iconName='bi-pencil'
                buttonText='Edit'
                color='borderless'
                size='sm'
                disabled={
                  props.isEditingCalendarEventTemplate ||
                  !props.calendarEventTemplate
                }
                onClick={() => props.setIsEditingCalendarEventTemplate(true)}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
